import React, { useState, useEffect } from 'react';
import { Paper, Box, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Hooks personnalisés
import { useChatMessages } from './hooks/useChatMessages';
import { useChatApi } from './hooks/useChatApi';
import { useScrollToBottom } from './hooks/useScrollToBottom';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useSessionTracking } from '../../../../hooks/useSessionTracking';

// Composants
import ChatHeader from './components/ChatHeader';
import MessageList from './components/MessageList';
import SuggestedQuestions from './components/SuggestedQuestions';
import MessageInput from './components/MessageInput';

function ChatWindow({ subdomain, userPhoto, workspaceId, isPreviewMode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [useStreaming, setUseStreaming] = useState(true);
  // const [useStreaming, setUseStreaming] = useState(false);
  const [questionsExpanded, setQuestionsExpanded] = useState(true);
  const { trackChatMessage, trackPageView } = useAnalytics();
  const { incrementPageView, incrementMessage } = useSessionTracking({
    workspaceId,
    userId: 'anonymous', // ou récupérer depuis le contexte auth
    isPreviewMode,
  });

  // Hooks personnalisés
  const {
    messages,
    isTyping,
    setIsTyping,
    addUserMessage,
    addBotMessage,
    addStreamingMessage,
    updateStreamingMessage,
    addErrorMessage,
  } = useChatMessages(subdomain);

  const { sendMessage, sendStreamingMessage } = useChatApi(subdomain);

  const { messagesEndRef, inputRef } = useScrollToBottom(messages, isTyping);

  useEffect(() => {
    if (workspaceId && !isPreviewMode) {
      console.log('trackPageView', workspaceId);
      trackPageView(workspaceId);
      incrementPageView();
    }
  }, [workspaceId, trackPageView, incrementPageView, isPreviewMode]);

  const handleSendMessage = async (messageText) => {
    if (messageText.trim() === '') return;

    addUserMessage(messageText);
    setIsTyping(true);

    try {
      if (useStreaming) {
        const streamingMessageId = addStreamingMessage();
        const startTime = Date.now();

        await sendStreamingMessage(
          messageText,
          (text) => updateStreamingMessage(streamingMessageId, text),
          (text) => {
            updateStreamingMessage(streamingMessageId, text, true);
            const responseTime = Date.now() - startTime;
            if (workspaceId && !isPreviewMode) {
              trackChatMessage(workspaceId, messageText, responseTime);
              incrementMessage();
            }
          },
          (errorText) => {
            updateStreamingMessage(streamingMessageId, errorText, true);
            const responseTime = Date.now() - startTime;
            if (workspaceId && !isPreviewMode) {
              trackChatMessage(workspaceId, messageText, responseTime);
              incrementMessage();
            }
          }
        );
      } else {
        const response = await sendMessage(messageText);
        addBotMessage(response.reply);
      }
    } catch (error) {
      addErrorMessage(t('chatWindow.errorMessage'));
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Paper
      elevation={isMobile ? 1 : 3}
      sx={{
        height: '100%',
        minHeight: isMobile ? '500px' : '60vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMobile ? 2 : 5,
        overflow: 'hidden',
      }}
    >
      {/* <ChatHeader
        useStreaming={useStreaming}
        setUseStreaming={setUseStreaming}
      /> */}
      <MessageList
        messages={messages}
        isTyping={isTyping}
        userPhoto={userPhoto}
        messagesEndRef={messagesEndRef}
      />
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          minHeight: isMobile ? '120px' : 'auto',
        }}
      >
        <SuggestedQuestions
          expanded={questionsExpanded}
          setExpanded={setQuestionsExpanded}
          onSelectQuestion={handleSendMessage}
        />
        <MessageInput onSendMessage={handleSendMessage} inputRef={inputRef} />
      </Box>
    </Paper>
  );
}

export default ChatWindow;
